import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  type Control,
  type UseFormSetValue,
  type UseFormWatch,
} from "react-hook-form";
import { StyledDataGrid } from "@/components/DataGrid/ChibanKaokuNumberSearchStyleDataGrid";
import { type PropertySelectionRow, type MapPlotRequest } from "../../types";
import { createGridColumns } from "./gridColumns";
import { CAutocompleteMultiFreeSoloTextField } from "@/components/TextField/CAutocompleteMultiFreeSoloTextField";
import { type FormData } from "./formSchema";
import { useAdditionalFieldsConfig } from "../../hooks/useAdditionalFieldsConfig";
import { CMapPlotRequestNameSelector } from "./CMapPlotRequestNameSelector";
import { convertStringToAdditionalFields } from "../../utils/additionalFieldsHelpers";

interface ListSaveTabProps {
  control: Control<FormData>;
  setValue: UseFormSetValue<FormData>;
  watch: UseFormWatch<FormData>;
  selectedProperties: PropertySelectionRow[];
  setSelectedProperties: React.Dispatch<
    React.SetStateAction<PropertySelectionRow[]>
  >;
  handleDelete: (id: number) => void;
  invalidPropertyIds: number[];
  existingRequests: MapPlotRequest[];
  existingTags: string[];
  isRequestsLoading: boolean;
}

export const ListSaveTab: React.FC<ListSaveTabProps> = ({
  control,
  setValue,
  watch,
  selectedProperties,
  handleDelete,
  invalidPropertyIds,
  existingRequests,
  existingTags,
  isRequestsLoading,
}) => {
  const { availableFields } = useAdditionalFieldsConfig();

  const gridColumns = createGridColumns(handleDelete, availableFields);

  // パフォーマンス最適化: additionalFieldsを事前にパースして各行に持たせる
  const selectedItems = useMemo(() => {
    return selectedProperties
      .filter((prop) => prop.selected)
      .map((prop) => ({
        ...prop,
        _parsedAdditionalFields: convertStringToAdditionalFields(
          prop.additionalFields,
          availableFields ?? undefined
        ),
      }));
  }, [selectedProperties, availableFields]);
  const [pageSize, setPageSize] = React.useState(100);
  const [page, setPage] = React.useState(0);
  const rowsPerPageOptions = [10, 50, 100];

  const handlePageChange = (newPage: number): void => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CMapPlotRequestNameSelector
              control={control}
              setValue={setValue}
              watch={watch}
              existingRequests={existingRequests}
              required={true}
              isLoading={isRequestsLoading}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
              タグ
            </Typography>
            <CAutocompleteMultiFreeSoloTextField
              name="tags"
              control={control}
              label="タグを追加"
              maxLength={20}
              options={existingTags}
              isLoading={isRequestsLoading}
              tooltipText={
                <>
                  区切り文字（スペース、カンマ、タブ、改行）で複数入力が可能です
                  <br />
                  コピー＆ペーストでも複数項目を一括で入力できます
                  <br />
                  既存の識別名を指定した場合、タグを入力すると上書きされます
                </>
              }
            />
          </Box>
        </Box>
      </Box>
      {invalidPropertyIds.length > 0 && (
        <Typography color="error" sx={{ mb: 2, fontWeight: "bold" }}>
          {invalidPropertyIds.length}
          件の物件に座標（緯度・経度）がありません。住所・所在だけの物件はファイル取り込みで登録してください。
        </Typography>
      )}
      {selectedItems.length > 0 ? (
        <Box sx={{ height: 600, width: "100%" }}>
          <StyledDataGrid
            disableColumnMenu
            disableSelectionOnClick
            rows={selectedItems}
            columns={gridColumns}
            pageSize={pageSize}
            page={page}
            rowCount={selectedItems.length}
            rowsPerPageOptions={rowsPerPageOptions}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            getRowHeight={() => "auto"}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                },
              },
            }}
            getRowClassName={(params) => {
              return invalidPropertyIds.includes(params.id as number)
                ? "invalid-row"
                : "";
            }}
            sx={{
              "& .invalid-row": {
                backgroundColor: "#ffebee",
                "&:hover": {
                  backgroundColor: "#ffcdd2",
                },
              },
            }}
          />
        </Box>
      ) : (
        <Typography color="text.secondary">
          保存する項目がチェックされていません。
        </Typography>
      )}
    </Box>
  );
};
